<template>
  <div class="pm-shell">
    <div v-if="!points.length" class="pm-empty">{{ $t('zones.patternMatrix.empty') }}</div>
    <template v-else>
      <p class="pm-caption">
        <i18n-t keypath="zones.patternMatrix.caption" scope="global">
          <template #title><strong>{{ $t('zones.patternMatrix.title') }}</strong></template>
        </i18n-t>
      </p>
      <PrioChart
        :option="option"
        :active="isActive"
        :height="410"
        name="PatternMatrix"
      />
      <ul class="chart-legend">
        <li v-for="it in legendItems" :key="it.label">
          <span class="legend-dot" :style="{ background: it.color }"></span>{{ it.label }}
        </li>
      </ul>
    </template>
  </div>
</template>

<script>
import { computed } from 'vue'

import { CEILINGS, THRESH, PATTERN_META } from '@/prioritization/settings'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { formatNumber, formatPercent } from '@/i18n/format'

import PrioChart from './ui/PrioChart.vue'
import { use } from 'echarts/core'
import { ScatterChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent, MarkLineComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
use([ScatterChart, GridComponent, TooltipComponent, LegendComponent, MarkLineComponent, CanvasRenderer])

// Sprint 7 — normalised axes. Each zone is plotted at
//   X = engagement / zone's own Emax × 100
//   Y = conversion / zone's own Cmax × 100
// so the chart compares EVERY zone to its own demonstrated ceiling,
// not to a single shared sector cross. The pattern classification
// thresholds (LOW_ENGAGEMENT = 0.40 × Emax etc.) collapse to fixed
// percentages on the normalised scale, since they were already
// proportional to each zone's ceiling. The "ceiling cross" sits at
// (100, 100) and represents "you've reached the level your zone has
// shown it can hit" — same target for every zone, on the same scale.
//
// Trade-off: the axes no longer read in raw engagement / conversion
// percentage. The tooltip still surfaces the absolute numbers for the
// analytical user.

export default {
  name: 'PatternMatrix',
  components: { PrioChart },
  props: {
    priorities: Array,
    // The eCeil / cCeil / lowEngagement / lowConversion props are
    // legacy from the absolute-axes implementation. They're no longer
    // used for positioning (the normalised view uses each zone's own
    // ceiling, read off the priority record) but are kept as accepted
    // props so existing callers don't break.
    lowEngagement: { type: Number, default: () => THRESH.LOW_ENGAGEMENT },
    lowConversion: { type: Number, default: () => THRESH.LOW_CONVERSION },
    eCeil: { type: Number, default: () => CEILINGS.Emax },
    cCeil: { type: Number, default: () => CEILINGS.Cmax },
    isActive: { type: Boolean, default: true },
    // Optional cross-chart annotation: { [zoneName]: { icon, label, color } }
    // from the strategicPositioning composer. When present, the tooltip
    // adds a "Strategic role" line so the Prio user sees the Intel-tab
    // verdict for the same zone without leaving the chart. Optional so
    // production callers that haven't wired it yet still work.
    strategicByZone: { type: Object, default: () => ({}) },
  },
  setup(props){
    const { t } = useI18n()
    // English keeps its own text and formats exactly as before; Dutch uses the
    // translations (focus.pattern.* for the shared pattern names) and format.js.
    const isEn = () => currentLanguage() === 'en'
    const pctText = (v) => (isEn() ? `${v}%` : formatPercent(v, { decimals: 1 }))
    const grouped = computed(() => {
      const g = Object.fromEntries(Object.keys(PATTERN_META).map(k => [k, []]))
      for (const p of (props.priorities || [])) {
        const key = PATTERN_META[p?.patternKind] ? p.patternKind : 'neutral'   // <- fallback to neutral, not strong
        g[key].push(p)
      }
      return g
    })

    const points = computed(()=> Object.values(grouped.value).flat())

    // {zoneName → patternKind} lookup so the tooltip can pull the
    // pattern's plain-language sentence (PATTERN_META[kind].plain) for
    // the hovered dot.
    const patternByZone = computed(() => {
      const out = {}
      for (const p of (props.priorities || [])) {
        if (p?.zone) out[p.zone] = p.patternKind
      }
      return out
    })

    // Legend labels — emoji prefix stripped so the chart legend reads
    // as plain text. The pattern emoji still appear on the Prio cards
    // and in the matrix tooltip where they earn the space, but in the
    // legend strip they cluttered the visual rhythm. Strip pattern:
    // leading non-space chars + the single space after them.
    // Legend rendered as HTML below the chart (see template) — wraps tight, no
    // reserved-space gap; card height equalised with its sibling via flexbox.
    const legendItems = computed(() => Object.entries(PATTERN_META).map(([k, m]) => ({
      label: isEn() ? m.legend.replace(/^\S+\s/, '') : t(`focus.pattern.${k}.legend`),
      color: m.color,
    })))

    // Sector best-practice values, in % terms. Every priority record
    // now carries the sector ceiling on zoneEmax / zoneCmax (since
    // self-calibration was retired); we read the first record's
    // values — they're identical across zones inside the same sector.
    // Fallback to the DIY-default 50% / 25% if no priorities yet.
    const sectorEmaxPct = computed(() => {
      const first = (props.priorities || [])[0]
      const v = first?.zoneEmax
      return (typeof v === 'number' && v > 0) ? v * 100 : 50
    })
    const sectorCmaxPct = computed(() => {
      const first = (props.priorities || [])[0]
      const v = first?.zoneCmax
      return (typeof v === 'number' && v > 0) ? v * 100 : 25
    })

    // Each dot carries 4 values:
    //   0,1 → absolute engagement % / conversion % (raw, used for position)
    //   2   → visitors (drives symbol size)
    //   3   → zone name (label + tooltip header)
    const mkSeries = (key) => ({
      // Series name must match the legend entry (echarts links them).
      // Use the emoji-stripped form so the bottom legend stays clean.
      name: PATTERN_META[key].legend.replace(/^\S+\s/, ''),
      type: 'scatter',
      symbolSize: d => Math.max(8, Math.min(32, Math.sqrt(d[2]))),
      itemStyle: {
        color: PATTERN_META[key].color,
        borderColor: '#fff',
        borderWidth: 1.5,
      },
      label: {
        show: true,
        formatter: (p) => p.data[3],
        position: 'top',
        color: '#3c4b59',
        fontSize: 11,
        fontWeight: 600,
      },
      emphasis: {
        itemStyle: { borderColor: '#1a2a36', borderWidth: 2 },
        label: { show: true, fontWeight: 700, color: '#1a2a36' },
      },
      data: grouped.value[key].map(p => ([
        +(p.engagementRate * 100).toFixed(1),
        +(p.conversionRate * 100).toFixed(1),
        p.visitors,
        p.zone,
      ])),
    })

    const series = computed(() => Object.keys(PATTERN_META).map(k => mkSeries(k)))

    // Axis maxima = sector best practices (so the dashed cross lands
    // at the top-right corner of the visible area). Whole-number
    // rounded UP with +2pp extra padding so:
    //   - axis labels never show decimals (e.g. 26.25 → 27)
    //   - zone dot labels (which sit ABOVE each dot) have headroom
    //   - zones that happen to exceed sector best practices still
    //     plot inside the chart, not clipped at the edge
    const dataMaxX = computed(() => {
      const all = (props.priorities || []).map(p => (p.engagementRate || 0) * 100)
      return all.length ? Math.max(...all) : 0
    })
    const dataMaxY = computed(() => {
      const all = (props.priorities || []).map(p => (p.conversionRate || 0) * 100)
      return all.length ? Math.max(...all) : 0
    })
    const xMax = computed(() => Math.ceil(Math.max(sectorEmaxPct.value, dataMaxX.value) + 2))
    const yMax = computed(() => Math.ceil(Math.max(sectorCmaxPct.value, dataMaxY.value) + 2))

    // Sector-best-practice cross — two dashed green lines at the
    // sector Emax / Cmax values. ONE label "sector best practices"
    // sits at the top-right intersection so the manager knows what
    // the cross represents.
    const sectorGuides = computed(() => ({
      name: '__guides__',
      type: 'scatter',
      data: [],
      silent: true,
      tooltip: { show: false },
      symbolSize: 0,
      z: 2,
      markLine: {
        symbol: 'none',
        animation: false,
        lineStyle: { color: '#136808', type: 'dashed', width: 1.2 },
        data: [
          {
            // Vertical line at sector Emax — no label (the label is
            // attached to the horizontal Y-line below at its LEFT end,
            // so it sits near the y-axis instead of being crammed
            // against the right edge of the chart).
            xAxis: sectorEmaxPct.value,
            label: { show: false },
          },
          {
            yAxis: sectorCmaxPct.value,
            name: t('zones.patternMatrix.sector'),
            label: {
              show: true,
              // 'start' = left end of the horizontal Y-line (at x=0,
              // y=sectorCmax) → label sits near the y-axis where
              // there's open space, not near the right edge where
              // dots cluster. The offset shifts the bubble a few
              // pixels INTO the chart so the white background
              // doesn't clip into the y-axis ticks/labels.
              position: 'start',
              offset: [42, -2],
              align: 'left',
              verticalAlign: 'bottom',
              color: '#136808',
              fontSize: 11,
              fontWeight: 600,
              backgroundColor: 'rgba(255,255,255,0.9)',
              padding: [3, 6],
              borderRadius: 4,
              formatter: t('zones.patternMatrix.sector'),
            },
          },
        ],
      },
    }))

    const option = computed(() => ({
      // Title + subtext intentionally dropped — the caption paragraph
      // above the chart carries the same information. Removing the
      // in-chart title gives the plot area more vertical room.

      // Grid padding: extra room on top + right so dot labels (zone
      // names that sit ABOVE each dot) + the "sector best practices"
      // label don't clip when zones sit near the chart edges.
      grid: {
        top: 24,
        left: 60,
        right: 60,
        bottom: 56,            /* room for the x-axis name only; legend is HTML */
        containLabel: true,
      },

      tooltip: {
        trigger: 'item',
        confine: true,
        appendToBody: false,
        formatter: (p) => {
          const [eng, conv, v, name] = p.data;
          const strat = props.strategicByZone?.[name]
          const stratLine = strat
            ? `<br/><span style="color:${strat.color}">${t('zones.patternMatrix.role')} · ${strat.icon} ${strat.label}</span>`
            : ''
          const kind = patternByZone.value[name]
          const meta = PATTERN_META[kind]
          const plain = meta && (isEn() ? meta.plain : t(`zones.patternMatrix.plain.${kind}`))
          const plainLine = (meta && meta.plain)
            ? `<br/><span style="color:${meta.color};font-style:italic">${plain}</span>`
            : ''
          return [
            `<b>${name}</b>`,
            plainLine,
            stratLine,
            `<br/>${t('zones.patternMatrix.engagement', { v: pctText(eng) })}`,
            `<br/>${t('zones.patternMatrix.conversion', { v: pctText(conv) })}`,
            `<br/>${t('zones.patternMatrix.visitors', { v: isEn() ? v.toLocaleString() : formatNumber(v) })}`,
          ].join('')
        }
      },

      // Axes show raw engagement / conversion percentages. The dashed
      // cross at (sector Emax, sector Cmax) is the "target" reference;
      // each zone's distance to it = its remaining headroom.
      //
      // axisLabel.formatter hides any tick labels ABOVE the sector
      // ceiling — when a zone outperforms the sector and the axis
      // extends past the cross, we don't want to label that overshoot
      // region. The plot area extends; the labels stop at the ceiling.
      xAxis: {
        name: t('zones.patternMatrix.xAxis'),
        nameLocation: 'middle',
        nameGap: 36,
        nameTextStyle: { color: '#6b7a87', fontSize: 12, fontWeight: 500 },
        min: 0, max: xMax.value,
        axisLabel: {
          formatter: (v) => v <= sectorEmaxPct.value ? (isEn() ? Math.round(v) + '%' : formatPercent(Math.round(v))) : '',
          margin: 10,
          hideOverlap: true,
        },
        splitLine: { show: true, lineStyle: { color: '#E5EDF6' } }
      },

      yAxis: {
        name: t('zones.patternMatrix.yAxis'),
        nameLocation: 'middle',
        nameGap: 50,
        nameRotate: 90,
        nameTextStyle: { color: '#6b7a87', fontSize: 12, fontWeight: 500 },
        min: 0, max: yMax.value,
        axisLabel: {
          formatter: (v) => v <= sectorCmaxPct.value ? (isEn() ? Math.round(v) + '%' : formatPercent(Math.round(v))) : '',
          margin: 10,
          hideOverlap: true,
        },
        splitLine: { show: true, lineStyle: { color: '#E5EDF6' } }
      },

      // legend intentionally omitted — rendered as HTML below the chart.
      series: [
        ...series.value,
        sectorGuides.value,
      ],

    }))
    return { points, option, legendItems }
  }
}
</script>

<style scoped>
/* Mirrors StrategicPositioningChart's .sp-shell so the two charts
   look identical when shown side-by-side on the Intelligence tab. */
.pm-shell {
  background: #fff;
  border: 1px solid #e6edf3;
  border-radius: 14px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.03);
  padding: 18px 18px 6px;
  margin-bottom: 24px;
}

/* HTML legend below the chart (matches StrategicPositioningChart). */
.chart-legend{
  list-style: none;
  margin: 6px 0 8px;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px 14px;
  font-size: 11px;
  color: #7a8895;
}
.chart-legend li{ display: inline-flex; align-items: center; gap: 5px; }
.legend-dot{ width: 9px; height: 9px; border-radius: 50%; flex: 0 0 auto; }

.pm-caption {
  font-size: 12px;
  color: #6b7a87;
  line-height: 1.5;
  margin: 0 0 8px;
}

.pm-empty {
  color: #9aa7b3;
  text-align: center;
  padding: 32px 0;
  font-style: italic;
}
</style>
